import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../utils/api';
import RichTextEditor from '../components/RichTextEditor';
import './CreateHarvestingPostPage.css';

const CreateHarvestingPostPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pages, setPages] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    imageUrl: '',
    pageSlug: new URLSearchParams(location.search).get('pageSlug') || 'introduction',
    publishDate: new Date().toISOString().slice(0, 16),
    unpublishDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16)
  });

  useEffect(() => {
    const fetchPages = async () => {
      try {
        const response = await api.get('/api/harvesting-pages');
        setPages(response.data.pages || []);
      } catch (err) {
        setPages([]);
      }
    };
    fetchPages();
  }, []);

  useEffect(() => {
    if (isEditing) {
      fetchPost();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchPost = async () => {
    try {
      const response = await api.get(`/api/harvesting-posts/${id}`);
      const post = response.data;
      setFormData({
        title: post.title,
        content: post.content,
        imageUrl: post.imageUrl || '',
        pageSlug: post.pageSlug || 'introduction',
        publishDate: new Date(post.publishDate).toISOString().slice(0, 16),
        unpublishDate: new Date(post.unpublishDate).toISOString().slice(0, 16)
      });
    } catch (err) {
      setError('Error loading post');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (isEditing) {
        await api.put(`/api/harvesting-posts/${id}`, formData);
      } else {
        await api.post('/api/harvesting-posts', formData);
      }
      navigate('/staff/harvesting');
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving post');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-harvesting-post-container">
      <div className="create-post-breadcrumb">
        <button className="btn-back-link" onClick={() => navigate('/staff/harvesting')}>
          ← Harvesting Dashboard
        </button>
      </div>

      <div className="harvesting-header">
        <h1>{isEditing ? 'Edit Blog Post' : 'Create Blog Post'}</h1>
      </div>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit} className="post-form">
        <div className="post-form-grid">
          <div className="form-group inline-label">
            <label>Title</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="form-group inline-label">
            <label>Image URL</label>
            <input
              type="url"
              name="imageUrl"
              value={formData.imageUrl}
              onChange={handleInputChange}
              placeholder="https://example.com/image.jpg"
            />
          </div>
          <div className="form-group inline-label">
            <label>Page / Topic</label>
            <select name="pageSlug" value={formData.pageSlug} onChange={handleInputChange}>
              <option value="introduction">Introduction (Main Feed)</option>
              {pages
                .filter((p) => p.slug !== 'introduction')
                .map((p) => (
                  <option key={p._id} value={p.slug}>{p.title}</option>
                ))}
            </select>
          </div>
        </div>
        <div className="form-group">
          <label>Content</label>
          <RichTextEditor
            value={formData.content}
            onChange={(value) => setFormData({ ...formData, content: value })}
            placeholder="Write your post content here..."
          />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Publish Date (Toronto Time)</label>
            <input
              type="datetime-local"
              name="publishDate"
              value={formData.publishDate}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Unpublish Date (Toronto Time)</label>
            <input
              type="datetime-local"
              name="unpublishDate"
              value={formData.unpublishDate}
              onChange={handleInputChange}
              required
            />
          </div>
        </div>
        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Saving...' : (isEditing ? 'Update Post' : 'Create Post')}
          </button>
          <button type="button" className="btn-secondary" onClick={() => navigate('/staff/harvesting')}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateHarvestingPostPage;
